import React from "react";
// seo
import Helmet from "react-helmet";
import { NavLink, Link, useLocation } from "react-router-dom";

import Section from "../../components/shared-ui/Section/Section";
import Accordion from "../../components/shared-ui/Accordion/Accordion";

import PEDLayout from "./PEDLayout";
import PEDHeroHeader from "../../components/HeroHeader/PEDHeroHeader";

import "../../css/PED/DopteletForChildren.scss";

export default function DopteletForChildren() {
  const showPhreesiaModal = (e) => {
    e.preventDefault();
    let modal = document.querySelector("#phreesia-modal");
    document.body.classList.add("no-scroll");
    modal.classList.remove("fadeOut");
    modal.style.display = "block";
    modal.classList.add("fadeIn");
    modal.style.height = "100%";
    modal.querySelector(".exitBtn").addEventListener("click", function() {
      modal.style.display = "none";
      document.body.classList.remove("no-scroll");
    });
    modal
      .querySelector(".modal-overlay")
      .addEventListener("click", function() {
        modal.style.display = "none";
        document.body.classList.remove("no-scroll");
      });
  };
  return (
    <React.Fragment>
      <Helmet>
        <title>DOPTELET® (avatrombopag) for Chronic & Persistent ITP in Children</title>
        <meta name="description" content="For children with persistent or chronic ITP: Explore treatment medications like DOPTELET® and Doptelet Sprinkle (avatrombopag), a thrombopoietin receptor agonist (TPO- RA)." />
      </Helmet>
      <PEDLayout pageClass="ped-doptelet-for-children">
        <PEDHeroHeader
          extraClass="large-hero inner-bg home-hero"
          markup={
            <>
              <p className="eyebrow">
                Doptelet is a prescription medicine used to help treat low blood
                platelet counts in children with persistent or chronic immune
                thrombocytopenia (ITP) when other treatments have not worked
                well enough.
              </p>
              <h1>FAST ACTING AND LONG LASTING</h1>
              <p>
                Doptelet and Doptelet Sprinkle are once-a-day oral medications
                that help raise your child’s platelet counts with no treatment
                injections and no extra doctor’s visits for treatment. Also, the
                type of food they eat won’t impact how treatment works.
                Doptelet and Doptelet Sprinkle are not used to make platelet
                counts normal.
              </p>
            </>
          }
          maxWidth="525"
          bgImage={[
            "/images/ped/hero/home-hero-desktop.jpg",
            "/images/ped/hero/home-hero-desktop.jpg",
          ]}
          bgImageMobile={[
            "/images/ped/hero/home-hero.png",
            "/images/ped/hero/home-hero@2x.png",
          ]}
        />
        <Section sectionClass="violator">
          <h2>Doptelet and Doptelet Sprinkle</h2>
          <p>
            Doptelet Sprinkle is indicated for patients aged ≥1 and &lt;6 years.
            Doptelet tablets are indicated for patients aged 6 and older.
            Doptelet Sprinkle oral granules that come in a bottle are not the
            same dosage form as Doptelet tablets that come in a blister card and
            cannot be directly substituted for each other.
          </p>
        </Section>

        <Section sectionClass="section-padding pb-0 coming-soon">
          <div className="coming-soon-cta">
            <h2>Coming soon: Doptelet Sprinkle</h2>
            <p>
              Doptelet Sprinkle has been approved for persistent or chronic
              pediatric ITP for kids 1 year and older to less than 6 years—Sobi
              is working to make it available as soon as possible.
            </p>
          </div>
        </Section>

        <Section sectionClass="section-padding doptelet-difference">
          <h2>What makes Doptelet or Doptelet Sprinkle different?</h2>
          <h3>
            Doptelet and Doptelet Sprinkle are oral treatments that can give
            pediatric patients with persistent or chronic ITP a platelet count
            lift anytime, anywhere, and with any food
          </h3>
          <p>
            Your child’s doctor will adjust the dose of Doptelet or Doptelet
            Sprinkle based on platelet levels reached and will check your
            child’s platelet counts weekly until their platelet counts reach
            50,000 per microliter, then monthly after that, and for at least 4
            weeks after stopping treatment. Before starting Doptelet or Doptelet
            Sprinkle, tell your child’s doctor about all the medicines they
            take, as their doctor may adjust the starting dose.
          </p>
          <div className="orange-border">
            <div className="d-flex flex-column flex-lg-row">
              <div className="orange-card">
                <div>
                  <img
                    src="/images/ped/icons/icon-food-restrictions@2x.png"
                    alt="No food restrictions"
                    className="ped-icon"
                  />
                  <h4>No food-type restrictions</h4>
                </div>
                <p>
                  Doptelet tablet is a thrombopoietin receptor agonist (TPO-RA)
                  that must be taken with food and can be taken at any time.
                  Because food won’t impact the effectiveness of treatment, your
                  child may have more flexibility with meal choices and
                  schedules.
                </p>
                <p>
                  The effectiveness of Doptelet Sprinkle also isn’t impacted by
                  food, but it must be taken with a soft food or liquid like
                  applesauce or orange juice.
                </p>
                <p>
                  <strong>
                    See the{" "}
                    <Link
                      to={{
                        pathname: "/themes/pdf/Instructions-for-Use.pdf"
                      }}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <span>Instructions for Use</span>
                    </Link>{" "}
                    for a full list of recommended soft foods and liquids.
                  </strong>
                </p>
              </div>
              <div className="orange-card">
                <div>
                  <img
                    src="/images/ped/icons/icon-liver@2x.png"
                    alt="No food restrictions"
                    className="ped-icon"
                  />
                  <h4>No additional risk of liver damage</h4>
                </div>
                <p>
                  Doptelet and Doptelet Sprinkle don’t require additional liver
                  function monitoring because in clinical trials they were not
                  shown to lead to liver damage (or “hepatotoxicity,” in medical
                  terms).
                </p>
              </div>
              <div className="orange-card">
                <div>
                  <img
                    src="/images/ped/icons/icon-injections@2x.png"
                    alt="No food restrictions"
                    className="ped-icon"
                  />
                  <h4>No treatment injections or infusions</h4>
                </div>
                <p>
                  Doptelet is a TPO-RA available in oral form (as a tablet or
                  oral granules) that can be taken anywhere.
                </p>
              </div>
            </div>
            <div className="d-flex flex-column flex-lg-row btop justify-content-center">
              <div className="orange-card">
                <div>
                  <img
                    src="/images/ped/icons/icon-eye-exams@2x.png"
                    alt="No food restrictions"
                    className="ped-icon"
                  />
                  <h4>No eye exams</h4>
                </div>
                <p>
                  Doptelet and Doptelet Sprinkle don’t require eye exams before
                  starting or during treatment.
                </p>
              </div>
              <div className="orange-card" style={{ margin: 0 }}>
                <div>
                  <img
                    src="/images/ped/icons/icon-paperwork@2x.png"
                    alt="No food restrictions"
                    className="ped-icon"
                  />
                  <h4>Fits their schedule</h4>
                </div>
                <p>
                  As long as it’s at the same time each day, Doptelet or
                  Doptelet Sprinkle can be taken at any time that works best for
                  your child and your family. Before starting Doptelet or
                  Doptelet Sprinkle, tell your child’s doctor about all the
                  medicines they take, as their doctor may adjust the starting
                  dose.
                </p>
              </div>
            </div>
          </div>
          <div className="safety-of-doptelet">
            <div className="">
              <h2 className="mb-2">
                The safety of Doptelet has been established over 7 years in adult patients*
              </h2>
              <p>
                *Doptelet is approved for use in adults with chronic ITP (FDA approved in 2019). See full indication details below.
              </p>
            </div>

            <img src="/images/ped/safety-of-doptelet.png" alt="" />
          </div>
        </Section>

        <Section sectionClass="choose-doptelet">
          <h2>
            Choose Doptelet or Doptelet Sprinkle for a platelet count lift that
            works fast and lasts
          </h2>
          <p>
            <strong>
              In the 3-month clinical trial, Doptelet and Doptelet Sprinkle were
              shown to:
            </strong>
          </p>
          <div className="orange-border">
            <div className="d-flex flex-column flex-lg-row">
              <div className="orange-card">
                <h4>LIFT PLATELETS</h4>
                <p>
                  Raise platelet counts to <strong>50,000</strong> per
                  microliter or more for at least 2 consecutive weeks of the
                  3-month trial.
                </p>
                <p>
                  This was seen in 44 of 54 children taking Doptelet or Doptelet
                  Sprinkle, compared to 0 of 21 on placebo.
                </p>
              </div>
              <div className="orange-card">
                <h4>ACT FAST</h4>
                <p>
                  Lift platelet counts to 50,000 per microliter or more in as few as <strong>8 days</strong>.
                </p>
                <p>By Day 8, 30 of 54 children on Doptelet or Doptelet Sprinkle had platelet counts of 50,000 per microliter or more, compared with 0 of 21 people in the placebo group.
                </p>
              </div>
              <div className="orange-card">
                <h4>AND LAST</h4>
                <p>
                  In at least <strong>6 out of the last 8 weeks</strong>, 15 of
                  54 children on Doptelet or Doptelet Sprinkle had a platelet
                  count of 50,000 per microliter or more without the need for
                  rescue treatment, compared with 0 of 21 on placebo.
                </p>
              </div>
            </div>
          </div>
          <Accordion
            title="More results from the pediatric ITP clinical trial"
            id="more-results"
            className="scroll-to-section mb-0"
            markup={
              <>
                <ul>
                  <li>
                    75 children with persistent or chronic immune
                    thrombocytopenia (aged 1 to &lt;18 years) were evaluated in
                    a 3-month clinical trial for Doptelet or Doptelet Sprinkle
                    (clinical trials for a rare disease usually have smaller
                    sample sizes than non-rare disease trials). During the trial
                    (core phase), 54 patients received Doptelet or Doptelet
                    Sprinkle, while 21 did not receive Doptelet or Doptelet
                    Sprinkle
                  </li>
                  <li>
                    {" "}
                    27.8% (15/54) of patients taking Doptelet or Doptelet
                    Sprinkle achieved an increase in platelet counts during at
                    least 6 of the last 8 weeks of the core study in the absence
                    of rescue therapy vs 0% of patients not taking Doptelet or
                    Doptelet Sprinkle
                  </li>
                  <li>
                    81.5% (44/54) of patients taking Doptelet or Doptelet
                    Sprinkle reached at least 2 consecutive platelet counts of
                    50,000 per microliter or more in the absence of rescue
                    therapy vs 0% of patients not taking Doptelet or Doptelet
                    Sprinkle
                  </li>
                  <li>
                    Most of the patients included in the trial (&gt;65%) had
                    tried 3 or more ITP treatments, and the trial also involved
                    both patients who had and had not used a thrombopoietin
                    receptor agonist (TPO-RA), which is the same type of
                    medicine as Doptelet and Doptelet Sprinkle
                  </li>
                  <li>
                    During the trial, there were children taking Doptelet or
                    Doptelet Sprinkle who were compared with those not taking
                    Doptelet or Doptelet Sprinkle. Those taking Doptelet or
                    Doptelet Sprinkle were able to keep platelet counts at or
                    above 50,000 platelets per microliter for an average of half
                    of the weeks (48.9%) in the absence of rescue therapy vs
                    those not taking Doptelet or Doptelet Sprinkle (1.2%)
                  </li>
                  <li>
                    On Day 8 of the trial, 55.6% (30/54) of patients taking
                    Doptelet or Doptelet Sprinkle reached at least 50,000
                    platelets per microliter or more in the absence of rescue
                    therapy. Those who were not taking Doptelet or Doptelet
                    Sprinkle did not reach 50,000 platelets per microliter
                  </li>
                  <li>
                    7% of children who received Doptelet or Doptelet Sprinkle
                    needed rescue therapy, while 43% of children who did not
                    receive Doptelet or Doptelet Sprinkle needed rescue therapy
                  </li>
                </ul>
              </>
            }
          />
        </Section>

        <Section sectionClass="section-padding theres-a-chance">
          <img src="/images/ped/icons/theres-a-chance-plane@2x.png" alt="" />
          <h2>
            With Doptelet or Doptelet Sprinkle, your child may experience side
            effects
          </h2>
          <div className="d-flex flex-column flex-md-row">
            <div>
              <h3>Serious side effects</h3>
              <p>
                Doptelet or Doptelet Sprinkle may cause serious side effects including blood clots. People with persistent or chronic ITP and people with certain blood clotting conditions may have an increased risk of developing blood clots. Tell your healthcare provider right away if you get signs and symptoms of a blood clot, including:
              </p>
              <ul>
                <li>Swelling, pain, or tenderness in their legs</li>
                <li>Shortness of breath</li>
                <li>Chest pain</li>
                <li>Fast heartbeat</li>
                <li>Stomach (abdominal) pain or tenderness</li>
              </ul>
            </div>
            <div>
              <h3>Common side effects</h3>
              <p>
                The most common side effects of Doptelet or Doptelet Sprinkle in children with persistent or chronic ITP are viral infection, runny nose, cough, fever, and pain in the mouth or throat.
              </p>
              <p>
                <strong>
                  These are not all the possible side effects of Doptelet and
                  Doptelet Sprinkle. Talk to your child’s doctor if any side
                  effect bothers your child or does not go away.
                </strong>
              </p>
            </div>
          </div>
        </Section>

        <Section sectionClass="section-padding take-doptelet blue-cta-card">
          <div className="take-doptelet-card blue-cta-card-content">
            <div className="d-flex flex-column flex-lg-row">
              <div>
                <h2>Learn how to take Doptelet and Doptelet Sprinkle</h2>
                <p>See how to prepare and administer the medicine.</p>
              </div>
              <div>
                <a
                  className="btn btn-primary btn-arrow"
                  href="/pediatric-itp/taking-doptelet-for-children/"
                >
                  <span>Taking Doptelet</span>
                </a>
              </div>
            </div>
          </div>
        </Section>

        <Section sectionClass="specialist-near-you">
          <div className="d-flex flex-column flex-md-row specialist-near-you-card">
            <div className="specialist-content">
              <h2>Find a persistent or chronic ITP specialist near you</h2>
              <p>
                One of the most important steps in getting care for your child
                is finding the right provider in your area.
              </p>
              <a
                className="btn btn-primary"
                href="#"
                onClick={showPhreesiaModal}
              >
                Find a specialist
              </a>
              <p>
                Sobi is not responsible for the choices or actions that you or
                others take because of information provided by this free tool.
              </p>
            </div>
            <div className="specialist-icon">
              <img src="/images/ped/icons/icon-soft-serve@2x.png" alt="" />
            </div>
          </div>
        </Section>
      </PEDLayout>
    </React.Fragment>
  );
}
